import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { HelpTip } from "@/components/HelpTip";
import { Plus, Pencil, Trash2, Gift, BadgePercent, Save } from "lucide-react";

function GiftCardMargins() {
  const [brands, setBrands] = useState([]);
  const [cfg, setCfg] = useState({ default_master: 2, default_dealer: 1, brands: {} });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    api.get("/settings/reloadly-giftcard-margins").then((r) => setCfg({ default_master: r.data.default_master, default_dealer: r.data.default_dealer, brands: r.data.brands || {} })).catch(() => {});
    api.get("/reloadly/giftcards/products", { params: { country: "IT" } })
      .then((r) => setBrands((r.data.products || []).slice().sort((a, b) => ((a.brand?.brandName || a.productName || "")).localeCompare(b.brand?.brandName || b.productName || ""))))
      .catch((e) => setErr(formatApiErrorDetail(e.response?.data?.detail) || "Catalogo Reloadly non disponibile"))
      .finally(() => setLoading(false));
  }, []);

  const setDefault = (k, v) => setCfg((c) => ({ ...c, [k]: v }));
  const setBrandOv = (pid, role, v) => setCfg((c) => {
    const b = { ...(c.brands || {}) };
    const entry = { ...(b[pid] || {}) };
    if (v === "" || v == null) delete entry[role]; else entry[role] = parseFloat(v);
    if (Object.keys(entry).length) b[pid] = entry; else delete b[pid];
    return { ...c, brands: b };
  });

  const save = async () => {
    setSaving(true);
    try {
      const payload = { default_master: parseFloat(cfg.default_master) || 0, default_dealer: parseFloat(cfg.default_dealer) || 0, brands: cfg.brands };
      const { data } = await api.put("/settings/reloadly-giftcard-margins", payload);
      setCfg({ default_master: data.default_master, default_dealer: data.default_dealer, brands: data.brands || {} });
      toast.success("Margini gift card salvati");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Errore salvataggio"); }
    finally { setSaving(false); }
  };

  const filtered = brands.filter((b) => !search || ((b.brand?.brandName || b.productName || "").toLowerCase().includes(search.toLowerCase())));

  return (
    <Card className="glow-border p-5 space-y-5" data-testid="giftcard-margins-card">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="font-head text-xl font-bold flex items-center gap-2"><Gift className="w-5 h-5 text-primary" /> Margini Gift Card</h2>
          <p className="text-sm text-muted-foreground mt-0.5">Margine di default per ruolo e override per singolo brand. L'agenzia/agente ha sempre 0%.</p>
        </div>
        <Button onClick={save} disabled={saving} className="gap-1" data-testid="giftcard-margins-save"><Save className="w-4 h-4" />{saving ? "Salvo…" : "Salva margini"}</Button>
      </div>

      <div className="grid sm:grid-cols-2 gap-4 max-w-md">
        <div>
          <Label className="flex items-center gap-1"><BadgePercent className="w-3.5 h-3.5" /> Default Master (%)</Label>
          <Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="gc-default-master" value={cfg.default_master} onChange={(e) => setDefault("default_master", e.target.value)} />
        </div>
        <div>
          <Label className="flex items-center gap-1"><BadgePercent className="w-3.5 h-3.5" /> Default Dealer (%)</Label>
          <Input type="number" step="0.1" min="0" className="mt-1.5" data-testid="gc-default-dealer" value={cfg.default_dealer} onChange={(e) => setDefault("default_dealer", e.target.value)} />
        </div>
      </div>

      {err && <p className="text-sm text-destructive" data-testid="gc-margins-error">{err}</p>}
      {loading && <p className="text-sm text-muted-foreground">Carico i brand…</p>}

      {!loading && !err && brands.length > 0 && (
        <div className="space-y-3">
          <Input className="max-w-xs" placeholder="Cerca brand…" data-testid="gc-brand-search" value={search} onChange={(e) => setSearch(e.target.value)} />
          <div className="max-h-[420px] overflow-y-auto rounded-lg border border-border">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Brand</TableHead><TableHead>Master % (override)</TableHead><TableHead>Dealer % (override)</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {filtered.map((b) => {
                  const pid = String(b.productId);
                  const ov = cfg.brands?.[pid] || {};
                  return (
                    <TableRow key={pid} data-testid={`gc-brand-row-${pid}`}>
                      <TableCell className="font-medium">{b.brand?.brandName || b.productName}</TableCell>
                      <TableCell>
                        <Input type="number" step="0.1" min="0" className="w-28" data-testid={`gc-brand-master-${pid}`}
                          value={ov.master ?? ""} placeholder={String(cfg.default_master)} onChange={(e) => setBrandOv(pid, "master", e.target.value)} />
                      </TableCell>
                      <TableCell>
                        <Input type="number" step="0.1" min="0" className="w-28" data-testid={`gc-brand-dealer-${pid}`}
                          value={ov.dealer ?? ""} placeholder={String(cfg.default_dealer)} onChange={(e) => setBrandOv(pid, "dealer", e.target.value)} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
          <p className="text-xs text-muted-foreground">Lascia vuoto un campo per usare il valore di default. {brands.length} brand disponibili.</p>
        </div>
      )}
    </Card>
  );
}

const EMPTY = { label: "", digital: false, commission_rate: 0, agent_rate: 0 };

export default function AdminCategories() {
  const [cats, setCats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => api.get("/categories").then((r) => setCats(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (c) => { setEditId(c.id); setForm({ label: c.label, digital: !!c.digital, commission_rate: c.commission_rate || 0, agent_rate: c.agent_rate || 0 }); setOpen(true); };

  const save = async (e) => {
    e.preventDefault();
    const payload = { label: form.label, digital: form.digital, commission_rate: form.commission_rate, agent_rate: form.agent_rate };
    try {
      if (editId) await api.put(`/categories/${editId}`, payload);
      else await api.post("/categories", payload);
      toast.success(editId ? "Categoria aggiornata" : "Categoria creata");
      setOpen(false); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore");
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/categories/${id}`); toast.success("Categoria eliminata"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Errore"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-categories-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs font-medium tracking-wider uppercase text-muted-foreground">Catalogo</p>
          <h1 className="font-head text-3xl font-bold tracking-tight mt-1">Categorie prodotti</h1>
          <p className="text-sm text-muted-foreground mt-1">Il guadagno del dealer è il margine di prezzo o la ripartizione di rete: le categorie non generano provvigioni.</p>
        </div>
        <div className="flex items-center gap-2">
          <HelpTip cat="provvigioni" />
          <Button onClick={openNew} className="gap-2" data-testid="category-add-button"><Plus className="w-4 h-4" /> Nuova categoria</Button>
        </div>
      </div>

      <Card className="glow-border">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Categoria</TableHead><TableHead>Chiave</TableHead><TableHead>Tipo</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {cats.map((c) => (
              <TableRow key={c.id} data-testid={`category-row-${c.key}`}>
                <TableCell className="font-medium">{c.label}</TableCell>
                <TableCell className="font-mono text-xs text-muted-foreground">{c.key}</TableCell>
                <TableCell><Badge variant="outline">{c.digital ? "Digitale" : "Fisico"}</Badge></TableCell>
                <TableCell className="flex gap-1">
                  <Button size="icon" variant="ghost" onClick={() => openEdit(c)} data-testid={`category-edit-${c.key}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="icon" variant="ghost" onClick={() => remove(c.id)} data-testid={`category-delete-${c.key}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </TableCell>
              </TableRow>
            ))}
            {cats.length === 0 && <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-8">Nessuna categoria.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </Card>

      <GiftCardMargins />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="category-dialog">
          <DialogHeader>
            <DialogTitle>{editId ? "Modifica categoria" : "Nuova categoria"}</DialogTitle>
            <DialogDescription>Definisci nome e tipo della categoria.</DialogDescription>
          </DialogHeader>
          <form onSubmit={save} className="space-y-4">
            <div><Label>Nome categoria</Label><Input required className="mt-1.5" data-testid="category-label-input" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="es. Assicurazioni" /></div>
            <div className="flex items-center justify-between rounded-lg border border-border p-3">
              <div><Label>Prodotto digitale</Label><p className="text-xs text-muted-foreground mt-0.5">Se attivo usa la tassazione digitale allo checkout.</p></div>
              <Switch checked={form.digital} onCheckedChange={(v) => setForm({ ...form, digital: v })} data-testid="category-digital-switch" />
            </div>
            <DialogFooter><Button type="submit" data-testid="category-save-button">Salva</Button></DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
